/**
 * Generic configurable multi-step form modal.
 * Powers: Culture Files, We Outside (Events), Reenactment Talent.
 *
 * config = {
 *   kind: 'culture-files' | 'events' | 'reenactment',
 *   intro: { eyebrow, headline, blurb, cta },
 *   steps: Step[],
 *   buildPayload: (form) => { full_name, email, phone, city, state, data, tags,
 *                              photos, video_file_id, video_url },
 *   success: { title, message },
 * }
 *
 * Step = {
 *   key: string,
 *   title: string,
 *   subtitle?: string,
 *   fields: Field[],
 *   validate?: (form) => string | "",  // returns error string or empty
 * }
 *
 * Field = {
 *   key: string,
 *   label: string,
 *   type: 'text'|'email'|'tel'|'number'|'textarea'|'select'|'checkboxes'|'radio'
 *         |'photos'|'video'|'tags'|'note',
 *   required?: boolean,
 *   placeholder?: string,
 *   options?: string[],            // for select / radio / checkboxes
 *   maxLength?: number,
 *   note?: string,                 // for type 'note'
 *   min?: number, max?: number,
 *   minPhotos?, maxPhotos?,        // for type 'photos'
 *   tagPool?: string[],            // suggested chips for type 'tags'
 * }
 */

import { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Upload,
  Trash2,
  Video,
  Link as LinkIcon,
  Plus,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHOTO_MAX_DIM = 1600;
const MAX_VIDEO_BYTES = 50 * 1024 * 1024;

async function resizeImageToDataURL(file) {
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });
  let { width, height } = img;
  const scale = Math.min(1, PHOTO_MAX_DIM / Math.max(width, height));
  width = Math.round(width * scale);
  height = Math.round(height * scale);
  const c = document.createElement("canvas");
  c.width = width;
  c.height = height;
  c.getContext("2d").drawImage(img, 0, 0, width, height);
  return c.toDataURL("image/jpeg", 0.85);
}

const inputCls =
  "w-full bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF]/40 px-4 py-3 text-white placeholder-white/30 outline-none transition-colors";

function Label({ children }) {
  return (
    <label className="block font-accent text-[11px] tracking-[0.22em] text-white/60 mb-2">
      {children}
    </label>
  );
}

// ============================================================
// Field renderers
// ============================================================
function FieldRenderer({ field, value, onChange, onPhotos, onVideo, busy, videoProgress }) {
  if (field.type === "note") {
    return (
      <div className="text-sm text-white/65 leading-relaxed border-l-2 border-[#FF2D9C]/60 pl-3 italic">
        {field.note}
      </div>
    );
  }
  if (field.type === "text" || field.type === "email" || field.type === "tel") {
    return (
      <div data-testid={`field-${field.key}`}>
        <Label>{field.label}{field.required ? " *" : ""}</Label>
        <input
          type={field.type === "tel" ? "tel" : field.type}
          className={inputCls}
          value={value || ""}
          maxLength={field.maxLength || 200}
          placeholder={field.placeholder || ""}
          onChange={(e) => onChange(field.key, e.target.value)}
          data-testid={`input-${field.key}`}
        />
      </div>
    );
  }
  if (field.type === "number") {
    return (
      <div data-testid={`field-${field.key}`}>
        <Label>{field.label}{field.required ? " *" : ""}</Label>
        <input
          type="number"
          className={inputCls}
          min={field.min ?? 0}
          max={field.max ?? 999}
          value={value ?? ""}
          placeholder={field.placeholder || ""}
          onChange={(e) => onChange(field.key, e.target.value)}
          data-testid={`input-${field.key}`}
        />
      </div>
    );
  }
  if (field.type === "textarea") {
    const len = (value || "").length;
    const max = field.maxLength || 4000;
    return (
      <div data-testid={`field-${field.key}`}>
        <Label>
          {field.label}
          {field.required ? " *" : ""} ({len}/{max})
        </Label>
        <textarea
          rows={field.rows || 5}
          className={inputCls + " resize-none min-h-[120px]"}
          value={value || ""}
          maxLength={max}
          placeholder={field.placeholder || ""}
          onChange={(e) => onChange(field.key, e.target.value)}
          data-testid={`input-${field.key}`}
        />
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <div data-testid={`field-${field.key}`}>
        <Label>{field.label}{field.required ? " *" : ""}</Label>
        <select
          className={inputCls}
          value={value || ""}
          onChange={(e) => onChange(field.key, e.target.value)}
          data-testid={`input-${field.key}`}
        >
          <option value="">— select —</option>
          {(field.options || []).map((o) => {
            const v = typeof o === "string" ? o : o.value;
            const l = typeof o === "string" ? o : o.label;
            return (
              <option key={v} value={v}>
                {l}
              </option>
            );
          })}
        </select>
      </div>
    );
  }
  if (field.type === "radio") {
    return (
      <div data-testid={`field-${field.key}`}>
        <Label>{field.label}{field.required ? " *" : ""}</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {(field.options || []).map((o) => {
            const v = typeof o === "string" ? o : o.value;
            const l = typeof o === "string" ? o : o.label;
            const active = value === v;
            return (
              <button
                key={v}
                type="button"
                onClick={() => onChange(field.key, v)}
                data-testid={`radio-${field.key}-${v.replace(/\s+/g, "-").toLowerCase()}`}
                className={`text-left px-4 py-3 border transition-all text-sm ${
                  active
                    ? "border-[#FF2D9C] bg-[#FF2D9C]/10 text-white"
                    : "border-white/10 bg-white/[0.02] text-white/75 hover:border-white/30"
                }`}
              >
                {l}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  if (field.type === "checkboxes") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (v) => {
      const next = arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
      onChange(field.key, next);
    };
    return (
      <div data-testid={`field-${field.key}`}>
        <Label>{field.label}{field.required ? " *" : ""}</Label>
        <div className="flex flex-wrap gap-2">
          {(field.options || []).map((o) => {
            const v = typeof o === "string" ? o : o.value;
            const l = typeof o === "string" ? o : o.label;
            const active = arr.includes(v);
            return (
              <button
                key={v}
                type="button"
                onClick={() => toggle(v)}
                data-testid={`chk-${field.key}-${v.replace(/\s+/g, "-").toLowerCase()}`}
                className={`px-3 py-1.5 font-accent text-[11px] tracking-[0.22em] border transition-all ${
                  active
                    ? "border-[#00E5FF] text-[#00E5FF] bg-[#00E5FF]/10"
                    : "border-white/10 text-white/70 hover:border-white/30"
                }`}
              >
                {l.toUpperCase()}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  if (field.type === "tags") {
    return <TagsField field={field} value={value} onChange={onChange} />;
  }
  if (field.type === "photos") {
    return (
      <PhotosField
        field={field}
        value={value || []}
        onPhotos={onPhotos}
        onChange={onChange}
      />
    );
  }
  if (field.type === "video") {
    return (
      <VideoField
        field={field}
        value={value || {}}
        onVideo={onVideo}
        onChange={onChange}
        busy={busy}
        progress={videoProgress}
      />
    );
  }
  return null;
}

function TagsField({ field, value, onChange }) {
  const arr = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState("");
  const add = (t) => {
    const v = (t || "").trim().toLowerCase();
    if (!v) return;
    if (arr.includes(v)) return;
    onChange(field.key, [...arr, v]);
  };
  const remove = (t) => onChange(field.key, arr.filter((x) => x !== t));
  return (
    <div data-testid={`field-${field.key}`}>
      <Label>{field.label}{field.required ? " *" : ""}</Label>
      <div className="flex flex-wrap gap-2 mb-2">
        {arr.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 px-2 py-1 border border-[#FF2D9C]/40 bg-[#FF2D9C]/10 text-[11px] font-accent tracking-widest text-[#FF2D9C]"
          >
            #{t}
            <button
              type="button"
              onClick={() => remove(t)}
              data-testid={`tag-remove-${t}`}
              className="hover:text-white"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      {field.tagPool && field.tagPool.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {field.tagPool
            .filter((t) => !arr.includes(t))
            .map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => add(t)}
                data-testid={`tag-suggest-${t}`}
                className="px-2 py-1 border border-white/10 hover:border-[#00E5FF]/60 text-[11px] font-accent tracking-widest text-white/60 hover:text-[#00E5FF]"
              >
                +{t}
              </button>
            ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          className={inputCls + " flex-1"}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="add a custom tag and press Enter"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add(draft);
              setDraft("");
            }
          }}
          data-testid={`input-${field.key}-draft`}
        />
        <button
          type="button"
          onClick={() => {
            add(draft);
            setDraft("");
          }}
          className="px-3 border border-white/15 text-white/70 hover:text-white hover:border-white/30"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function PhotosField({ field, value, onPhotos, onChange }) {
  const ref = useRef(null);
  const min = field.minPhotos ?? 0;
  const max = field.maxPhotos ?? 8;
  const arr = value;
  const remove = (i) =>
    onChange(field.key, arr.filter((_, j) => j !== i));
  return (
    <div data-testid={`field-${field.key}`}>
      <Label>
        {field.label}
        {field.required ? " *" : ""} ({arr.length}/{max} · min {min})
      </Label>
      {field.note && (
        <p className="text-xs text-white/55 mb-2 italic">{field.note}</p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {arr.map((p, i) => (
          <div
            key={i}
            className="relative aspect-square border border-white/10 overflow-hidden group"
            data-testid={`${field.key}-preview-${i}`}
          >
            <img src={p.data} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => remove(i)}
              data-testid={`${field.key}-remove-${i}`}
              className="absolute top-2 right-2 w-8 h-8 bg-black/80 hover:bg-[#FF2D9C] text-white flex items-center justify-center opacity-0 group-hover:opacity-100"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {arr.length < max && (
          <button
            type="button"
            onClick={() => ref.current?.click()}
            data-testid={`${field.key}-add`}
            className="aspect-square border-2 border-dashed border-white/20 hover:border-[#00E5FF]/60 flex flex-col items-center justify-center gap-2 text-white/60 hover:text-[#00E5FF]"
          >
            <Upload className="w-6 h-6" />
            <span className="font-accent text-[11px] tracking-widest">
              ADD
            </span>
          </button>
        )}
      </div>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => onPhotos(field.key, e.target.files, max)}
        data-testid={`${field.key}-input`}
      />
    </div>
  );
}

function VideoField({ field, value, onVideo, onChange, busy, progress }) {
  const ref = useRef(null);
  const has = !!value.video_file_id;
  return (
    <div data-testid={`field-${field.key}`}>
      <Label>{field.label}{field.required ? " *" : ""}</Label>
      {field.note && (
        <p className="text-xs text-white/55 mb-3 italic">{field.note}</p>
      )}
      <div className="border border-white/10 bg-white/[0.02] p-4 mb-3">
        <div className="flex items-center gap-2 mb-3">
          <Video className="w-4 h-4 text-[#00E5FF]" />
          <span className="font-accent text-xs tracking-widest text-white/70">
            UPLOAD A FILE (MAX 50 MB)
          </span>
        </div>
        {has ? (
          <div className="flex items-center justify-between gap-3 bg-[#00E5FF]/10 border border-[#00E5FF]/40 p-3">
            <div className="flex items-center gap-3 min-w-0">
              <CheckCircle2 className="w-5 h-5 text-[#00E5FF] shrink-0" />
              <div className="text-sm text-white truncate">
                {value.video_filename || "Video uploaded"}
              </div>
            </div>
            <button
              type="button"
              onClick={() =>
                onChange(field.key, { ...value, video_file_id: null, video_filename: "" })
              }
              data-testid={`${field.key}-remove`}
              className="text-white/60 hover:text-[#FF2D9C] p-1"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => ref.current?.click()}
            disabled={busy}
            data-testid={`${field.key}-choose`}
            className="w-full border-2 border-dashed border-white/20 hover:border-[#00E5FF]/60 py-6 flex flex-col items-center gap-2 text-white/70 hover:text-[#00E5FF] disabled:opacity-60"
          >
            {busy ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="font-accent text-[11px] tracking-widest">
                  UPLOADING… {progress}%
                </span>
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                <span className="font-accent text-[11px] tracking-widest">
                  CHOOSE VIDEO FILE
                </span>
              </>
            )}
          </button>
        )}
        <input
          ref={ref}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={(e) => onVideo(field.key, e.target.files?.[0])}
          data-testid={`${field.key}-input`}
        />
      </div>
      <div className="flex items-center gap-3 mb-3">
        <div className="flex-1 h-px bg-white/10" />
        <span className="font-accent text-[11px] text-white/40 tracking-widest">
          OR
        </span>
        <div className="flex-1 h-px bg-white/10" />
      </div>
      <div className="border border-white/10 bg-white/[0.02] p-4">
        <div className="flex items-center gap-2 mb-3">
          <LinkIcon className="w-4 h-4 text-[#FF2D9C]" />
          <span className="font-accent text-xs tracking-widest text-white/70">
            PASTE A LINK (YOUTUBE / DRIVE / DROPBOX)
          </span>
        </div>
        <input
          type="url"
          className={inputCls}
          placeholder="https://…"
          value={value.video_url || ""}
          onChange={(e) =>
            onChange(field.key, {
              ...value,
              video_url: e.target.value,
              ...(e.target.value
                ? { video_file_id: null, video_filename: "" }
                : {}),
            })
          }
          data-testid={`${field.key}-url`}
          disabled={has}
        />
      </div>
    </div>
  );
}

// ============================================================
// Main modal
// ============================================================
export default function MultiStepFormModal({ open, onClose, config }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [videoBusy, setVideoBusy] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [showIntro, setShowIntro] = useState(true);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep(0);
        setForm({});
        setError("");
        setSuccess(null);
        setSubmitting(false);
        setShowIntro(true);
      }, 250);
    }
  }, [open]);

  const steps = config?.steps || [];
  const total = steps.length;

  const setField = (k, v) => {
    setError("");
    setForm((f) => ({ ...f, [k]: v }));
  };

  const handlePhotos = async (key, files, max) => {
    setError("");
    const arr = Array.from(files || []);
    const existing = form[key] || [];
    const slots = max - existing.length;
    if (slots <= 0) return;
    try {
      const out = [];
      for (const f of arr.slice(0, slots)) {
        if (!f.type.startsWith("image/")) continue;
        const data = await resizeImageToDataURL(f);
        out.push({ data, name: f.name });
      }
      setForm((f) => ({ ...f, [key]: [...existing, ...out] }));
    } catch (e) {
      setError("Could not read one of the photos.");
    }
  };

  const handleVideo = async (key, file) => {
    setError("");
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      setError("Please choose a video file.");
      return;
    }
    if (file.size > MAX_VIDEO_BYTES) {
      setError("Video is over 50 MB. Compress it or paste a link instead.");
      return;
    }
    setVideoBusy(true);
    setVideoProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/applications/video`, fd, {
        onUploadProgress: (p) => {
          if (p.total) setVideoProgress(Math.round((p.loaded / p.total) * 100));
        },
      });
      setForm((f) => ({
        ...f,
        [key]: {
          ...(f[key] || {}),
          video_file_id: res.data.file_id,
          video_filename: res.data.filename,
          video_url: "",
        },
      }));
    } catch (e) {
      setError("Video upload failed. Try again.");
    } finally {
      setVideoBusy(false);
    }
  };

  const validateStep = () => {
    setError("");
    const s = steps[step];
    if (!s) return "";
    // built-in validations
    for (const f of s.fields || []) {
      if (!f.required) continue;
      const v = form[f.key];
      if (f.type === "checkboxes" || f.type === "tags") {
        if (!Array.isArray(v) || v.length === 0)
          return `${f.label} is required.`;
        if (f.minChecked && v.length < f.minChecked)
          return `${f.label}: please check all ${f.minChecked} items.`;
        continue;
      }
      if (f.type === "photos") {
        if (!Array.isArray(v) || v.length < (f.minPhotos || 0))
          return `${f.label}: please upload at least ${f.minPhotos || 1} photo${
            (f.minPhotos || 1) > 1 ? "s" : ""
          }.`;
        continue;
      }
      if (f.type === "video") {
        if (!v?.video_file_id && !v?.video_url)
          return `${f.label}: please upload a video or paste a link.`;
        continue;
      }
      if (f.type === "email") {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim()))
          return `${f.label}: please enter a valid email.`;
        continue;
      }
      if (f.type === "number") {
        const n = Number(v);
        if (isNaN(n)) return `${f.label}: enter a number.`;
        if (f.min != null && n < f.min)
          return `${f.label}: must be at least ${f.min}.`;
        if (f.max != null && n > f.max)
          return `${f.label}: must be at most ${f.max}.`;
        continue;
      }
      if (!v || (typeof v === "string" && !v.trim()))
        return `${f.label} is required.`;
    }
    if (s.validate) {
      const r = s.validate(form);
      if (r) return r;
    }
    return "";
  };

  const next = () => {
    const e = validateStep();
    if (e) return setError(e);
    if (step < total - 1) setStep(step + 1);
  };
  const prev = () => {
    setError("");
    if (step > 0) setStep(step - 1);
  };

  const submit = async () => {
    const e = validateStep();
    if (e) return setError(e);
    setSubmitting(true);
    setError("");
    try {
      const payload = config.buildPayload(form);
      const res = await axios.post(`${API}/intake/${config.kind}`, payload);
      setSuccess(res.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(
        typeof d === "string"
          ? d
          : Array.isArray(d)
            ? d.map((x) => x.msg).join(", ")
            : "Submission failed. Try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const progressPct = useMemo(
    () => Math.round(((step + 1) / Math.max(total, 1)) * 100),
    [step, total],
  );

  if (!config) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          data-testid={`${config.kind}-modal`}
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-start sm:items-center justify-center overflow-y-auto p-0 sm:p-6"
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl bg-[#0d0d14] border border-white/10 min-h-screen sm:min-h-0 shadow-[0_30px_100px_rgba(0,229,255,0.12)]"
          >
            <button
              onClick={onClose}
              data-testid={`${config.kind}-close`}
              className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-black/50 hover:bg-[#FF2D9C] hover:text-white text-white/70 border border-white/10 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {success ? (
              <SuccessBlock
                config={config}
                data={success}
                onClose={onClose}
              />
            ) : showIntro ? (
              <IntroBlock
                config={config}
                onStart={() => setShowIntro(false)}
              />
            ) : (
              <>
                {/* Header + progress */}
                <div className="px-6 sm:px-10 pt-8 pb-5 border-b border-white/10">
                  <div className="font-accent text-xs text-[#FF2D9C] mb-2 tracking-[0.22em]">
                    AUTOMOTOVILLE · {config.intro.eyebrow}
                  </div>
                  <div className="flex items-baseline justify-between">
                    <h2
                      className="font-heading text-2xl sm:text-3xl font-black"
                      data-testid={`${config.kind}-step-title`}
                    >
                      {steps[step]?.title || ""}
                    </h2>
                    <span className="font-accent text-xs text-white/50">
                      STEP {step + 1} / {total}
                    </span>
                  </div>
                  {steps[step]?.subtitle && (
                    <p className="mt-3 text-sm text-white/60 leading-relaxed">
                      {steps[step].subtitle}
                    </p>
                  )}
                  <div className="mt-5 h-[2px] bg-white/10 overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-[#FF2D9C] to-[#00E5FF]"
                      initial={false}
                      animate={{ width: `${progressPct}%` }}
                      transition={{ duration: 0.35 }}
                    />
                  </div>
                </div>

                {/* Body */}
                <div className="px-6 sm:px-10 py-7 space-y-5 min-h-[340px]">
                  {(steps[step]?.fields || []).map((f) => (
                    <FieldRenderer
                      key={f.key}
                      field={f}
                      value={form[f.key]}
                      onChange={setField}
                      onPhotos={handlePhotos}
                      onVideo={handleVideo}
                      busy={videoBusy}
                      videoProgress={videoProgress}
                    />
                  ))}
                  {error && (
                    <div
                      data-testid={`${config.kind}-error`}
                      className="flex items-start gap-2 text-sm text-[#FF2D9C] border border-[#FF2D9C]/40 bg-[#FF2D9C]/10 px-4 py-3"
                    >
                      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-6 sm:px-10 py-5 border-t border-white/10 flex items-center justify-between gap-3 bg-black/40 sticky bottom-0">
                  <button
                    onClick={prev}
                    disabled={step === 0}
                    data-testid={`${config.kind}-prev`}
                    className="flex items-center gap-2 px-4 py-3 font-accent text-xs tracking-widest text-white/70 disabled:opacity-30 disabled:cursor-not-allowed hover:text-white transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  {step < total - 1 ? (
                    <button
                      onClick={next}
                      data-testid={`${config.kind}-next`}
                      className="flex items-center gap-2 bg-[#FF2D9C] text-white font-black px-8 py-3 uppercase tracking-[0.22em] text-sm glow-pink transition-all hover:scale-[1.02]"
                    >
                      Continue <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={submit}
                      disabled={submitting}
                      data-testid={`${config.kind}-submit`}
                      className="flex items-center gap-2 bg-[#FF2D9C] text-white font-black px-8 py-3 uppercase tracking-[0.22em] text-sm glow-pink transition-all hover:scale-[1.02] disabled:opacity-60"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />{" "}
                          {config.submitLabel || "Submit"}
                        </>
                      )}
                    </button>
                  )}
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function IntroBlock({ config, onStart }) {
  const i = config.intro;
  const isRich = !!(i.poster || i.pitch || i.accepts || i.process || i.note);

  if (!isRich) {
    return (
      <div
        className="px-6 sm:px-12 py-16 text-center"
        data-testid={`${config.kind}-intro`}
      >
        <div className="font-accent text-xs text-[#FF2D9C] mb-4 tracking-[0.22em]">
          {i.eyebrow}
        </div>
        <h2 className="font-heading text-3xl sm:text-5xl font-black leading-[1.05] max-w-2xl mx-auto">
          {i.headline}
        </h2>
        {i.blurb && (
          <p className="text-white/65 mt-6 max-w-xl mx-auto leading-relaxed">
            {i.blurb}
          </p>
        )}
        <button
          onClick={onStart}
          data-testid={`${config.kind}-start`}
          className="mt-10 inline-flex items-center gap-2 bg-[#FF2D9C] text-white font-black px-10 py-4 uppercase tracking-[0.22em] text-sm glow-pink transition-all hover:scale-[1.03]"
        >
          <Sparkles className="w-4 h-4" /> {i.cta}
        </button>
      </div>
    );
  }

  // Rich landing-style intro for marquee shows (Independent Creators & Filmmakers)
  return (
    <div
      className="text-left"
      data-testid={`${config.kind}-intro`}
    >
      {/* Hero — poster + headline */}
      <div className="relative overflow-hidden border-b border-white/10">
        {i.poster && (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${i.poster})` }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a12]/60 via-[#0a0a12]/85 to-[#0a0a12]" />
        <div className="relative px-6 sm:px-12 py-16 sm:py-20">
          <div className="font-accent text-xs text-[#FF2D9C] mb-4 tracking-[0.22em]">
            {i.eyebrow}
          </div>
          <h2 className="font-heading text-3xl sm:text-5xl md:text-6xl font-black leading-[1.02] max-w-3xl">
            {i.headline}
          </h2>
          {i.tagline && (
            <p className="mt-5 text-lg sm:text-xl text-[#00E5FF] max-w-2xl font-semibold leading-snug">
              {i.tagline}
            </p>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-6 sm:px-12 py-10 space-y-12">
        {/* Pitch */}
        {i.pitch && (
          <div className="space-y-4 max-w-3xl">
            {i.pitch.map((para, idx) => (
              <p
                key={idx}
                className="text-white/75 leading-relaxed text-base"
              >
                {para}
              </p>
            ))}
          </div>
        )}

        {/* Standards bar */}
        {i.standards && (
          <div className="border-l-2 border-[#FF2D9C] pl-5 max-w-3xl">
            <div className="font-accent text-[11px] tracking-[0.22em] text-[#FF2D9C] mb-1.5">
              MUST BE FULLY EDITED & READY FOR DISTRIBUTION
            </div>
            <p className="text-white/70 leading-relaxed text-sm">
              {i.standards}
            </p>
          </div>
        )}

        {/* What we accept grid */}
        {i.accepts && i.accepts.length > 0 && (
          <div>
            <div className="font-accent text-xs tracking-[0.22em] text-[#00E5FF] mb-4">
              / WHAT WE ACCEPT
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {i.accepts.map((a) => (
                <div
                  key={a}
                  className="border border-white/10 bg-white/[0.02] px-4 py-4 text-center"
                >
                  <span className="font-accent text-[11px] tracking-widest text-white/85">
                    {a.toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Process steps */}
        {i.process && i.process.length > 0 && (
          <div>
            <div className="font-accent text-xs tracking-[0.22em] text-[#00E5FF] mb-4">
              / HOW IT WORKS
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {i.process.map((p, idx) => (
                <div
                  key={p.title}
                  className="relative border border-white/10 bg-white/[0.02] p-5"
                >
                  <div className="font-heading text-3xl font-black text-[#FF2D9C] mb-2">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                  <div className="font-accent text-[11px] tracking-[0.22em] text-white mb-2">
                    {p.title.toUpperCase()}
                  </div>
                  <p className="text-white/65 text-sm leading-relaxed">
                    {p.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Closing punch / earn note */}
        {i.earn && (
          <div className="border border-[#00E5FF]/30 bg-[#00E5FF]/5 px-6 py-5 max-w-3xl">
            {i.earnTitle ? (
              <div className="font-heading font-black text-xl text-white mb-1">
                {i.earnTitle}
              </div>
            ) : null}
            <p className="text-white/85 text-base leading-relaxed">{i.earn}</p>
          </div>
        )}

        {/* CTA */}
        <div className="pt-2">
          <button
            onClick={onStart}
            data-testid={`${config.kind}-start`}
            className="inline-flex items-center gap-3 bg-[#FF2D9C] text-white font-black px-8 sm:px-12 py-4 uppercase tracking-[0.22em] text-sm sm:text-base glow-pink transition-all hover:scale-[1.02]"
          >
            <Sparkles className="w-4 h-4" /> {i.cta}
          </button>
        </div>

        {/* Optional footer note */}
        {i.note && (
          <div className="border-t border-white/10 pt-7 max-w-3xl">
            <div className="font-accent text-[11px] tracking-[0.22em] text-[#FF2D9C] mb-2">
              {i.note.label || "NOT READY YET?"}
            </div>
            <p className="text-white/70 text-sm leading-relaxed">
              {i.note.body}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function SuccessBlock({ config, data, onClose }) {
  return (
    <div
      className="px-6 sm:px-12 py-16 text-center"
      data-testid={`${config.kind}-success`}
    >
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#FF2D9C]/10 border border-[#FF2D9C]/40 pulse-ring mb-6">
        <CheckCircle2 className="w-10 h-10 text-[#FF2D9C]" />
      </div>
      <h3 className="font-heading text-3xl font-black">
        {config.success?.title || "Submitted."}
      </h3>
      <p className="text-white/70 mt-4 max-w-md mx-auto leading-relaxed">
        {config.success?.message ||
          "Thanks — we got it. A producer will be in touch."}
      </p>
      {data?.id && (
        <p className="text-white/40 font-accent text-[11px] tracking-widest mt-4">
          REFERENCE · {String(data.id).slice(0, 8).toUpperCase()}
        </p>
      )}
      <button
        onClick={onClose}
        data-testid={`${config.kind}-success-close`}
        className="mt-8 bg-[#FF2D9C] text-white font-black px-8 py-3 uppercase tracking-[0.22em] text-sm glow-pink transition-all hover:scale-[1.02]"
      >
        Close
      </button>
    </div>
  );
}
